import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layouts/Footer';
import Cookies from 'universal-cookie';
import {
  makeStyles,
  Grid,
  Typography,
  Box,
  Card,
  CardHeader,
  CardContent,
} from '@material-ui/core';
import Notification from '../../components/Notification';
import { downloadBalanceSummaryCsv } from '../../services/reportService';
import userService from '../../services/userService';
import Controls from '../../components/controls/Controls';

const drawerWidth = 250;
const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  content: {
    [theme.breakpoints.up('sm')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },
  pageContent: {
    margin: theme.spacing(1),
    padding: theme.spacing(1),
  },
}));

function BalanceSummary() {
  const cookies = new Cookies();
  const classes = useStyles();
  const currentUser = cookies.get('currentUser');
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [allUserAccounts, setAllUserAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(currentUser?.accountid || '');

  useEffect(() => {
    if (currentUser?.roleid === 1) {
      userService
        .getUserAccounts()
        .then((accounts) => setAllUserAccounts(accounts || []))
        .catch(() => {});
    }
  }, [currentUser?.roleid]);

  const notification = (message, type) => {
    setNotify({ isOpen: true, message, type });
  };

  const timeOutSet = () => {
    notification('Your Session Expired....Try again...', 'error');
    setTimeout(() => {
      cookies.remove('currentUser');
      window.location.href = '/';
    }, 2000);
  };

  const handleDownload = () => {
    if (!startDate || !endDate) {
      notification('Please select both start date and end date', 'error');
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      notification('Start date must be on or before end date', 'error');
      return;
    }

    setLoading(true);
    const accountId = currentUser?.roleid === 1 && selectedAccountId ? Number(selectedAccountId) : undefined;

    downloadBalanceSummaryCsv(startDate, endDate, accountId)
      .then(() => {
        notification('Balance summary CSV downloaded successfully', 'success');
      })
      .catch((error) => {
        const msg =
          error?.response?.data?.error_msg ||
          (typeof error?.response?.data === 'string' ? error.response.data : null) ||
          error?.message ||
          'Download failed';
        if (msg === 'Invalid Token') timeOutSet();
        else notification(msg, 'error');
      })
      .finally(() => setLoading(false));
  };

  return (
    <div>
      <Helmet title="Balance Summary" />
      <main className={classes.content}>
        <Grid item lg={12} xs={12}>
          <Typography variant="h5" gutterBottom className="breadcrumbs pb-0">
            Balance Summary
          </Typography>
          <Box variant="subtitle1" className="welcome-msg" style={{ marginBottom: 16 }}>
            Download balance and activity summary CSV for a date range (Stripe Connect report).
          </Box>
        </Grid>

        <div className={classes.pageContent}>
          <Card className="tile-card">
            <CardHeader title="Download balance report" className="card-title mb-0" />
            <CardContent className="tile-title pt-0">
              <Grid container spacing={2} alignItems="center">
                {currentUser?.roleid === 1 && allUserAccounts.length > 0 && (
                  <Grid item xs={12} sm={4}>
                    <label className="mb-1 d-block">Account</label>
                    <select
                      className="select-box"
                      value={selectedAccountId}
                      onChange={(e) => setSelectedAccountId(e.target.value)}
                      style={{ minWidth: 200 }}
                    >
                      <option value="">My account</option>
                      {allUserAccounts
                        .filter((a) => a.roleid !== 1)
                        .map((acc) => (
                          <option key={acc.accountid} value={acc.accountid}>
                            {acc.accountname}
                          </option>
                        ))}
                    </select>
                  </Grid>
                )}
                <Grid item xs={12} sm={4}>
                  <label className="mb-1 d-block">Start date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="select-box"
                    style={{ padding: 8, minWidth: 180 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <label className="mb-1 d-block">End date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="select-box"
                    style={{ padding: 8, minWidth: 180 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controls.Button
                    type="button"
                    onClick={handleDownload}
                    text={loading ? 'Generating…' : 'Download CSV'}
                    className="link-red"
                    disabled={loading}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
      <Notification notify={notify} setNotify={setNotify} />
    </div>
  );
}

export default BalanceSummary;
